import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import { createSpendingTransaction } from "../../lib/api-modules/spending-transactions.js";
import {
  PAYMENT_METHOD_LABELS,
  type AccountSummary,
  type BudgetCategory,
  type CreateSpendingTransactionInput,
  type PaymentMethod,
} from "../../lib/api-modules/types.js";
import { dollarsToCents } from "../../lib/util/money.js";
import { ErrorMessage } from "../auth/error-message.js";

/**
 * <SpendingTransactionForm> — inline create form for spending tx.
 *
 * Required: date, account, amount (USD), merchant.
 * Optional: category (dropdown of active categories), payment method,
 * description, recurring flag, needs-review flag, tags (comma-sep).
 *
 * `source` is forced to "manual" by the server; we don't surface
 * the field at all.
 */

const PAYMENT_METHOD_OPTIONS: ReadonlyArray<{
  value: PaymentMethod | "";
  label: string;
}> = [
  { value: "", label: "—" },
  ...(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((v) => ({
    value: v,
    label: PAYMENT_METHOD_LABELS[v],
  })),
];

interface SpendingTransactionFormProps {
  readonly onClose: () => void;
  readonly defaultDate: string;
  readonly accounts: readonly AccountSummary[];
  readonly categories: readonly BudgetCategory[];
}

export function SpendingTransactionForm({
  onClose,
  defaultDate,
  accounts,
  categories,
}: SpendingTransactionFormProps) {
  const queryClient = useQueryClient();
  const activeCategories = categories.filter((c) => c.is_active !== false);
  const nonArchivedAccounts = accounts.filter((a) => a.is_archived !== true);

  const [date, setDate] = useState<string>(defaultDate);
  const [accountId, setAccountId] = useState<string>(
    nonArchivedAccounts[0]?.account_id ?? "",
  );
  const [amountDollars, setAmountDollars] = useState<string>("");
  const [merchant, setMerchant] = useState<string>("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
  const [description, setDescription] = useState<string>("");
  const [tags, setTags] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const cents = dollarsToCents(amountDollars);
      const trimmedMerchant = merchant.trim();
      const trimmedDescription = description.trim();
      const tagList = tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      const input: CreateSpendingTransactionInput = {
        date,
        account_id: accountId,
        amount_cents: cents,
        merchant: trimmedMerchant,
        is_recurring: isRecurring,
      };
      if (categoryId.length > 0) input.category_id = categoryId;
      if (paymentMethod !== "") input.payment_method = paymentMethod;
      if (trimmedDescription.length > 0) input.description = trimmedDescription;
      if (tagList.length > 0) input.tags = tagList;
      return createSpendingTransaction(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["spending-transactions"],
      });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save transaction. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    if (accountId.length === 0) {
      setInlineError("Account is required.");
      return;
    }
    const cents = dollarsToCents(amountDollars);
    if (cents <= 0) {
      setInlineError("Amount must be greater than 0.");
      return;
    }
    if (merchant.trim().length === 0) {
      setInlineError("Merchant is required.");
      return;
    }
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label="Add transaction"
      className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Add transaction</h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">Date</label>
          <input
            type="date"
            className={inputClass}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Amount (USD)
          </label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            className={inputClass}
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            disabled={mutation.isPending}
            placeholder="45.87"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium">Merchant</label>
          <input
            type="text"
            className={inputClass}
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            disabled={mutation.isPending}
            placeholder="Whole Foods"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Account</label>
          <select
            className={inputClass}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            disabled={mutation.isPending}
          >
            {nonArchivedAccounts.length === 0 ? (
              <option value="">— No accounts available —</option>
            ) : (
              nonArchivedAccounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.name} ({a.institution})
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Category</label>
          <select
            className={inputClass}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">— Uncategorized —</option>
            {activeCategories.map((c) => (
              <option key={c.category_id} value={c.category_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Payment method <span className="text-zinc-500">(optional)</span>
          </label>
          <select
            className={inputClass}
            value={paymentMethod}
            onChange={(e) =>
              setPaymentMethod(e.target.value as PaymentMethod | "")
            }
            disabled={mutation.isPending}
          >
            {PAYMENT_METHOD_OPTIONS.map((o) => (
              <option key={o.value || "none"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 pt-5">
          <input
            id="tx-recurring"
            type="checkbox"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
            disabled={mutation.isPending}
          />
          <label htmlFor="tx-recurring" className="text-xs">
            Recurring
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium">
            Description <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="text"
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium">
            Tags <span className="text-zinc-500">(comma-separated)</span>
          </label>
          <input
            type="text"
            className={inputClass}
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            disabled={mutation.isPending}
            placeholder="grocery, weekend"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Add transaction"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>

      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";
